import React, { useMemo } from "react";
import SectionHeader from "./SectionHeader";
import { formatPercent } from "./utils";

const GENDER_HEX_ROWS = [4, 5, 6, 7, 8, 9, 8, 7, 6, 5, 4];
const GENDER_SCALE = [0, 25, 50, 75, 100];

const genderIcon = (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

const personIcon = (
  <svg viewBox="0 0 24 24" aria-hidden="true" fill="currentColor">
    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
  </svg>
);

function GenderSection({ summary }) {
  const womenItem = summary.items?.find((item) => item.key === "female");
  const menItem = summary.items?.find((item) => item.key === "male");
  const womenCount = womenItem?.count ?? 0;
  const menCount = menItem?.count ?? 0;

  const gridRows = useMemo(() => {
    const totalSlots = GENDER_HEX_ROWS.reduce((sum, count) => sum + count, 0);
    const womenSlots = Math.round((summary.womenPercent / 100) * totalSlots);
    let filled = 0;

    return GENDER_HEX_ROWS.map((count) => {
      const row = Array.from({ length: count }, () => {
        const tone = filled < womenSlots ? "women" : "men";
        filled += 1;
        return tone;
      });
      return row;
    });
  }, [summary.womenPercent]);

  const headerExtra = (
    <div className="hr-dashboard__gender-header-legend">
      <span className="hr-dashboard__gender-header-item">
        <span className="hr-dashboard__legend-dot hr-dashboard__legend-dot--women" />
        Ж: {formatPercent(summary.womenPercent)}
      </span>
      <span className="hr-dashboard__gender-header-item">
        <span className="hr-dashboard__legend-dot hr-dashboard__legend-dot--men" />
        М: {formatPercent(summary.menPercent)}
      </span>
    </div>
  );

  return (
    <section className="hr-dashboard__card hr-dashboard__card--half hr-dashboard__card--gender">
      <SectionHeader
        icon={genderIcon}
        iconTone="pink"
        title="Гендерное соотношение"
        subtitle="Структура персонала"
        extra={headerExtra}
      />

      <div className="hr-dashboard__gender-layout">
        <div className="hr-dashboard__gender-visual">
          <div className="hr-dashboard__gender-hex">
            {gridRows.map((row, rowIndex) => (
              <div key={rowIndex} className="hr-dashboard__gender-hex-row">
                {row.map((tone, index) => (
                  <span
                    key={`${rowIndex}-${index}`}
                    className={`hr-dashboard__gender-grid-icon hr-dashboard__gender-grid-icon--${tone}`}
                  >
                    {personIcon}
                  </span>
                ))}
              </div>
            ))}
          </div>
          <div className="hr-dashboard__gender-totals">
            <div className="hr-dashboard__gender-total hr-dashboard__gender-total--women">
              <strong>{womenCount}</strong>
              <span>Женщин</span>
            </div>
            <div className="hr-dashboard__gender-total hr-dashboard__gender-total--men">
              <strong>{menCount}</strong>
              <span>Мужчин</span>
            </div>
          </div>
        </div>

        <div className="hr-dashboard__gender-departments">
          <p className="hr-dashboard__gender-departments-title">По отделениям (%)</p>
          {summary.departments?.length ? (
            <>
              <div className="hr-dashboard__gender-dept-list">
                {summary.departments.map((dept) => {
                  const women = Math.round(dept.womenPercent);
                  const men = Math.round(dept.menPercent);

                  return (
                    <div
                      key={dept.id ?? dept.code}
                      className="hr-dashboard__gender-dept-row"
                    >
                      <span
                        className="hr-dashboard__gender-dept-name"
                        title={dept.name || dept.code}
                      >
                        {dept.code || dept.name}
                      </span>
                      <div className="hr-dashboard__gender-dept-bar-wrap">
                        <div className="hr-dashboard__gender-dept-bar">
                          {dept.womenPercent > 0 && (
                            <div
                              className="hr-dashboard__gender-dept-segment hr-dashboard__gender-dept-segment--women"
                              style={{ width: `${dept.womenPercent}%` }}
                            />
                          )}
                          {dept.menPercent > 0 && (
                            <div
                              className="hr-dashboard__gender-dept-segment hr-dashboard__gender-dept-segment--men"
                              style={{ width: `${dept.menPercent}%` }}
                            />
                          )}
                          {dept.unknownPercent > 0 && (
                            <div
                              className="hr-dashboard__gender-dept-segment hr-dashboard__gender-dept-segment--unknown"
                              style={{ width: `${dept.unknownPercent}%` }}
                            />
                          )}
                        </div>
                        <span className="hr-dashboard__gender-dept-ratio">
                          {women}% / {men}%
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="hr-dashboard__gender-scale">
                <span className="hr-dashboard__gender-scale-spacer" />
                <div className="hr-dashboard__gender-scale-bar-wrap">
                  <div className="hr-dashboard__gender-scale-track">
                    {GENDER_SCALE.map((tick) => (
                      <span key={tick}>{tick}%</span>
                    ))}
                  </div>
                  <span className="hr-dashboard__gender-dept-ratio hr-dashboard__gender-dept-ratio--ghost" />
                </div>
              </div>
            </>
          ) : (
            <p className="hr-dashboard__empty-state">Нет данных по отделениям</p>
          )}
        </div>
      </div>
    </section>
  );
}

export default GenderSection;
